import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { formatDate } from "@/libs/utils";
import { ChevronDown, X } from "lucide-react";
import { useParams, useNavigate } from "react-router-dom";
import { useReadScenarioApprovalsHistory } from "@/libs/react-query/hooks/useScenarioApprovalsQuery";
import { SCENARIO_DECISION_LABELS, ScenarioDecision } from "@/consts/Scenario";

type Props = {
  disabled?: boolean;
};

export default function ScenarioApprovalHistoryButton({
  disabled = false,
}: Props) {
  const { group_id = "" } = useParams();
  const navigate = useNavigate();
  const { data } = useReadScenarioApprovalsHistory(group_id);

  if (!data?.scenario_approvals.length) return null;

  const renderStatus = (stage: number, finalDecision: ScenarioDecision) => {
    if (finalDecision === ScenarioDecision.APPROVED) {
      return (
        <span className="text-green text-[10px] font-normal">
          {SCENARIO_DECISION_LABELS[finalDecision]}됨
        </span>
      );
    } else if (finalDecision === ScenarioDecision.REJECTED) {
      return (
        <span className="text-pink text-[10px] font-normal">
          {SCENARIO_DECISION_LABELS[finalDecision]}됨
        </span>
      );
    } else if (finalDecision === ScenarioDecision.CANCELED) {
      return (
        <span className="text-pink text-[10px] font-normal">
          {SCENARIO_DECISION_LABELS[finalDecision]}됨
        </span>
      );
    } else {
      return (
        <span className="text-[13px] text-gray-A font-normal">{stage}단계</span>
      );
    }
  };

  const handleRowClick = (approvalId: string) => {
    navigate(`/approvals/${approvalId}`);
  };

  return (
    <Popover modal>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={`px-3 h-8 bg-green text-white hover:text-white flex items-center gap-2 rounded-sm text-sm font-semibold ${
            disabled ? "opacity-50 cursor-not-allowed" : "hover:bg-green/80"
          }`}
          disabled={disabled}
        >
          <span>시나리오 승인 내역</span>
          <ChevronDown className="w-4 h-4" />
        </Button>
      </PopoverTrigger>
      {data && (
        <PopoverContent className="w-[500px] p-0" align="end">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead></TableHead>
                <TableHead>승인 요청 시점</TableHead>
                <TableHead>요청자명</TableHead>
                <TableHead>승인 상태</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.scenario_approvals
                .sort((a, b) => b.created_at - a.created_at)
                .map((approval, index) => (
                  <TableRow
                    key={approval.approval_id}
                    className="cursor-pointer hover:bg-gray-50"
                    onClick={() => handleRowClick(approval.approval_id)}
                  >
                    <TableCell className="text-center">{index + 1}</TableCell>
                    <TableCell>
                      {formatDate(approval.created_at, "yyyy-MM-dd")}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Avatar className="size-4">
                          <AvatarImage src={approval.requested_by.img_url} />
                          <AvatarFallback>
                            {approval.requested_by.user_name.charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                        <span className="text-gray-A text-xs font-normal">
                          {approval.requested_by.user_name}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {renderStatus(
                        approval.current_stage,
                        approval.final_decision as ScenarioDecision
                      )}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </PopoverContent>
      )}
    </Popover>
  );
}
